import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ExternalLink, ShieldCheck } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'

/**
 * Global listener for the `bokap:limit-reached` window event dispatched by
 * lib/api.ts whenever a 402 response comes back. Decides whether to show
 * the "tenant limit / upgrade" copy or the "your assigned limit / contact
 * the workspace admin" copy based on the response body's scope field.
 *
 * Responses are normalized in three places:
 *  - quota-service (tenant pool 402): { error: '...' }
 *  - ai-gateway-service (per-member 402): { error: '...', code, scope: 'member', kind, details: { period_end, ... } }
 *  - chatbot fallbacks: { reply, limitReached, scope, kind }
 *
 * Anything else falls back to a generic "limit reached" message + Upgrade
 * link so we never leave a 402 unexplained.
 */

type Scope = 'tenant' | 'member' | 'unknown'
type Kind = 'tokens' | 'agents' | 'workflows' | 'transactions' | 'statements' | 'unknown'

interface LimitDetail {
  scope: Scope
  kind: Kind
  message: string
  periodEnd?: string | null
  used?: number
  limit?: number
  role?: string
}

function parseDetail(detail: { path: string; body: unknown; message: string }): LimitDetail {
  const body = detail.body as Record<string, unknown> | null
  const rawScope = body && typeof body.scope === 'string' ? body.scope : null
  const rawKind = body && typeof body.kind === 'string' ? body.kind : null
  const message =
    (body && typeof body.error === 'string' && body.error) ||
    (body && typeof body.reply === 'string' && body.reply) ||
    detail.message ||
    'Limit reached.'

  let scope: Scope = rawScope === 'member' ? 'member' : rawScope === 'tenant' ? 'tenant' : 'unknown'
  let kind: Kind = (() => {
    switch (rawKind) {
      case 'tokens':
      case 'agents':
      case 'workflows':
      case 'transactions':
      case 'statements':
        return rawKind
      default:
        return 'unknown'
    }
  })()

  // Heuristic fallback for legacy 402 routes that don't tag scope/kind.
  if (scope === 'unknown') {
    if (/transaction/i.test(message)) {
      scope = 'tenant'
      kind = 'transactions'
    } else if (/token/i.test(message)) {
      scope = 'tenant'
      kind = 'tokens'
    } else if (/statement/i.test(message)) {
      scope = 'tenant'
      kind = 'statements'
    } else {
      scope = 'tenant'
    }
  }

  const details = (body && typeof body.details === 'object' && body.details
    ? (body.details as Record<string, unknown>)
    : null) as Record<string, unknown> | null

  return {
    scope,
    kind,
    message,
    periodEnd: details && typeof details.period_end === 'string' ? details.period_end : null,
    used: details && typeof details.used === 'number' ? details.used : undefined,
    limit: details && typeof details.limit === 'number' ? details.limit : undefined,
    role: details && typeof details.role === 'string' ? details.role : undefined,
  }
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return null
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

const KIND_LABEL: Record<Kind, string> = {
  tokens: 'monthly AI tokens',
  agents: 'AI agents',
  workflows: 'workflows',
  transactions: 'monthly transactions',
  statements: 'monthly statements',
  unknown: 'usage',
}

export function LimitReachedDialog() {
  const [detail, setDetail] = useState<LimitDetail | null>(null)

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ path: string; body: unknown; message: string }>
      try {
        setDetail(parseDetail(ce.detail))
      } catch {
        setDetail({ scope: 'unknown', kind: 'unknown', message: 'Limit reached.' })
      }
    }
    window.addEventListener('bokap:limit-reached', handler)
    return () => window.removeEventListener('bokap:limit-reached', handler)
  }, [])

  const isMember = detail?.scope === 'member'
  const days = useMemo(() => daysUntil(detail?.periodEnd ?? null), [detail?.periodEnd])

  if (!detail) return null

  return (
    <Modal
      open={!!detail}
      onOpenChange={(v) => !v && setDetail(null)}
      title={isMember ? 'Your assigned limit is reached' : 'Workspace limit reached'}
      className="sm:max-w-md"
      footer={
        <>
          <Button variant="secondary" onClick={() => setDetail(null)}>
            Close
          </Button>
          {isMember ? (
            <Button asChild variant="primary">
              <a href="/team-management" onClick={() => setDetail(null)}>
                <ShieldCheck className="size-4" aria-hidden />
                View my limits
              </a>
            </Button>
          ) : (
            <Button asChild variant="primary">
              <a href="/subscriptions" onClick={() => setDetail(null)}>
                <ExternalLink className="size-4" aria-hidden />
                Upgrade plan
              </a>
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="flex gap-3 rounded-md bg-destructive/5 p-3 text-destructive">
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          <span>{detail.message}</span>
        </div>
        {isMember ? (
          <p className="text-muted-foreground">
            You&apos;ve reached the {KIND_LABEL[detail.kind]} limit assigned to your role
            {detail.role ? <span> ({detail.role})</span> : null}. Contact a workspace admin (or
            anyone with the &ldquo;Manage AI Gateway limits&rdquo; permission) to raise your limit.
            {days != null
              ? ` Token limits also reset automatically in ${days} day${days === 1 ? '' : 's'}.`
              : null}
          </p>
        ) : (
          <p className="text-muted-foreground">
            The workspace has consumed its {KIND_LABEL[detail.kind]} allowance for this period.
            {days != null
              ? ` It resets in ${days} day${days === 1 ? '' : 's'},`
              : ''}{' '}
            or upgrade the subscription to keep going.
          </p>
        )}
      </div>
    </Modal>
  )
}
